import BaseAPIService from './baseAPI';
import { PostListResponse, PostResponse, PostStatus } from '../types/api';

interface PostCreateRequest {
  topic: string;
  content: string;
  platforms?: string[];
  status?: string;
  scheduled_time?: string;
  image_url?: string;
  image_path?: string;
  image_credit?: string;
  image_search_terms?: string;
  platform_content?: Record<string, {
    platform: string;
    content: string;
    char_count?: number;
    char_limit?: number;
    within_limit?: boolean;
  }>;
}

interface PostUpdateRequest {
  topic?: string;
  content?: string;
  status?: string;
  scheduled_time?: string;
  image_url?: string;
  image_path?: string;
  image_credit?: string;
  platforms?: string[];
  platform_content?: PostCreateRequest['platform_content'];
}

interface SchedulePostRequest {
  scheduled_time: string;
  platforms?: string[];
  timezone?: string;
}

interface GetPostsParams {
  status?: PostStatus;
  skip?: number;
  limit?: number;
  page?: number;
  include_scheduling_info?: boolean;
}

interface ScheduledPostInfo {
  post_id: string;
  scheduled_time: string;
  time_remaining_seconds: number;
  time_remaining_formatted: string;
  is_scheduled_in_celery: boolean;
  celery_task_id?: string;
  platforms: string[];
}

interface ScheduledPostsResponse {
  data: ScheduledPostInfo[];
  total: number;
  status: string;
  message: string;
}

class PostsAPIService extends BaseAPIService {
  constructor() {
    super();
  }

  async getPosts(
    params?: GetPostsParams,
    includeSchedulingInfo?: boolean
  ): Promise<PostListResponse> {
    const { page, ...rest } = params ?? {};
    const limit = rest.limit ?? 100;
    const skip = rest.skip ?? (page ? (page - 1) * limit : 0);
    const queryParams = {
      ...rest,
      skip,
      limit,
      include_scheduling_info:
        rest.include_scheduling_info ??
        includeSchedulingInfo ??
        rest.status === 'SCHEDULED',
    };
    return this.get('/posts', { params: queryParams });
  }

  async getPost(id: string): Promise<PostResponse> {
    return this.get(`/posts/${id}`);
  }

  async createPost(data: PostCreateRequest): Promise<PostResponse> {
    return this.post('/posts', data);
  }

  async updatePost(id: string, data: PostUpdateRequest): Promise<PostResponse> {
    return this.put(`/posts/${id}`, data);
  }

  async deletePost(id: string): Promise<{ message: string }> {
    return this.delete(`/posts/${id}`);
  }

  async publishPost(id: string): Promise<PostResponse> {
    return this.post(`/posts/${id}/publish`);
  }

  async duplicatePost(id: string): Promise<PostResponse> {
    return this.post(`/posts/${id}/duplicate`);
  }

  // Scheduler-related methods
  async schedulePost(id: string, scheduleData: SchedulePostRequest): Promise<PostResponse> {
    return this.post(`/scheduler/schedule/${id}`, scheduleData);
  }

  async cancelSchedule(id: string): Promise<PostResponse> {
    return this.put(`/scheduler/cancel/${id}`, {});
  }

  async getUpcomingPosts(params?: GetPostsParams): Promise<PostListResponse> {
    return this.get('/scheduler/upcoming', { params });
  }

  async getScheduledPostsInfo(params?: Record<string, unknown>): Promise<ScheduledPostsResponse> {
    return this.get('/scheduler/scheduled-info', params);
  }

  async getNextPost(): Promise<PostResponse> {
    return this.get('/scheduler/next');
  }

  async getSchedulerStatus(): Promise<any> {
    return this.get('/scheduler/status');
  }

  async getCeleryTaskStatus(taskId: string): Promise<any> {
    return this.get(`/scheduler/celery-task/${taskId}`);
  }

  async resyncScheduler(): Promise<any> {
    return this.post('/scheduler/resync', {});
  }

  // Enhanced scheduling workflow
  async createAndSchedulePost(postData: PostCreateRequest, scheduleData: SchedulePostRequest): Promise<PostResponse> {
    // Step 1: Create post as DRAFT
    const createResponse = await this.createPost({
      ...postData,
      status: 'DRAFT'
    });
    
    if (!createResponse.data?.id) {
      throw new Error('Failed to create post');
    }
    
    // Step 2: Schedule the post
    const scheduleResponse = await this.schedulePost(createResponse.data.id, scheduleData);
    
    return scheduleResponse;
  }

  async bulkDelete(ids: string[]): Promise<{ message: string }> {
    return this.post('/posts/bulk-delete', { ids });
  }

  async bulkPublish(ids: string[]): Promise<{ message: string }> {
    return this.post('/posts/bulk-publish', { ids });
  }

  async bulkSchedule(ids: string[], scheduleData: SchedulePostRequest): Promise<{ message: string }> {
    return this.post('/posts/bulk-schedule', { ids, ...scheduleData });
  }

  async testConnection(): Promise<boolean> {
    try {
      const response: any = await this.get('/health');
      return response.status === 'success';
    } catch {
      return false;
    }
  }
}

export default new PostsAPIService(); 